import type { CaptureFileJSONObject } from '@ikegps/core-api';

import { IkeId } from '../../../id';
import type { CollectionModel } from '../collection.data';
import type {
	CollectionModelBrightnessCorrection,
	ModelStateBrightnessCorrection,
	ModelStateBrightnessCorrectionTag,
} from './brightnessCorrection';
import { CollectionModelGammaBrightnessCorrection } from './gamma.brightnessCorrection';

export type BrightnessCorrectionId = string;
export type ModelFieldStateBrightnessCorrection = BrightnessCorrectionId[];

function makeBrightnessCorrectionState(
	tag: ModelStateBrightnessCorrectionTag,
	id: string
): ModelStateBrightnessCorrection {
	switch (tag) {
		case 'gamma':
			return CollectionModelGammaBrightnessCorrection.state(id);
		default:
			throw new Error(`Unknown marker type: "${tag}"`);
	}
}

export class CollectionModelBrightnessCorrectionStateMutator {
	state: CollectionModelBrightnessCorrectionState;
	constructor(state: CollectionModelBrightnessCorrectionState) {
		this.state = state;
	}

	getState(): ModelFieldStateBrightnessCorrection {
		const collectionState = this.state.collection.state;

		const captureBrightnessCorrectionKey = CollectionModelBrightnessCorrectionState.captureBrightnessCorrectionId(
			this.state.captureId
		);
		const state = collectionState.getFieldValue(captureBrightnessCorrectionKey) as ModelFieldStateBrightnessCorrection;
		if (state == null) {
			return [];
		}

		return state.slice();
	}

	setState(state: ModelFieldStateBrightnessCorrection) {
		const collectionState = this.state.collection.state;
		const captureBrightnessCorrectionKey = CollectionModelBrightnessCorrectionState.captureBrightnessCorrectionId(
			this.state.captureId
		);
		collectionState.setFieldValue(captureBrightnessCorrectionKey, state);
	}

	create(tag: ModelStateBrightnessCorrectionTag, id = IkeId.generate()): BrightnessCorrectionId {
		const state = makeBrightnessCorrectionState(tag, id);
		return this.createWithState(state);
	}

	remove(brightnessCorrection: CollectionModelBrightnessCorrection) {
		const brightnessCorrectionIds = this.getState();
		const index = brightnessCorrectionIds.indexOf(brightnessCorrection.id);
		if (index === -1) {
			throw new Error(
				`Cannot remove brightness correction. Instance "${brightnessCorrection.id}" is missing from capture "${this.state.captureId}"`
			);
		}

		brightnessCorrectionIds.splice(index, 1);
		this.setState(brightnessCorrectionIds);

		delete this.state.brightnessCorrections[brightnessCorrection.id];
		brightnessCorrection.Mutator.destroyState();
	}

	clear() {
		const brightnessCorrections = this.state.values();
		for (const brightnessCorrection of brightnessCorrections) {
			const brightnessCorrectionIds = this.getState();
			const index = brightnessCorrectionIds.indexOf(brightnessCorrection.id);
			if (index === -1) {
				continue;
			}
			this.remove(brightnessCorrection);
		}
	}

	private createWithState(state: ModelStateBrightnessCorrection): BrightnessCorrectionId {
		const collectionState = this.state.collection.state;
		const captureId = this.state.captureId;

		const stateId = CollectionModelBrightnessCorrectionState.brightnessCorrectionId(captureId, state.id);
		if (collectionState.fields.get(stateId) != null) {
			throw new Error(`Brightness Correction "${stateId}" already exists`);
		}

		collectionState.setFieldValue(stateId, state);

		const brightnessCorrectionState: ModelFieldStateBrightnessCorrection = this.getState();
		brightnessCorrectionState.push(state.id);
		this.setState(brightnessCorrectionState);

		return state.id;
	}
}

const ID_PREFIX_BRIGHTNESS_CORRECTION = 'b';
const ID_PREFIX_CAPTURE_BRIGHTNESS_CORRECTION = 'bl';

export class CollectionModelBrightnessCorrectionState {
	static brightnessCorrectionId(captureId: string, brightnessCorrectionId: string): string {
		return `${ID_PREFIX_BRIGHTNESS_CORRECTION}:${captureId}:${brightnessCorrectionId}`;
	}

	static captureBrightnessCorrectionId(captureId: string): string {
		return `${ID_PREFIX_CAPTURE_BRIGHTNESS_CORRECTION}:${captureId}`;
	}

	static getCaptureBrightnessCorrectionId(key: string): string | null {
		if (key.startsWith(`${ID_PREFIX_CAPTURE_BRIGHTNESS_CORRECTION}:`)) {
			return key.substr(3);
		}

		return null;
	}
	Mutator: CollectionModelBrightnessCorrectionStateMutator = new CollectionModelBrightnessCorrectionStateMutator(this);
	id = IkeId.generate();
	collection: CollectionModel;
	captureId: string;

	brightnessCorrections: { [key: string]: CollectionModelBrightnessCorrection } = {};

	constructor(collection: CollectionModel, captureId: string) {
		this.collection = collection;
		this.captureId = captureId;
	}

	hasBrightnessCorrection(brightnessCorrectionId: string) {
		return this.state.indexOf(brightnessCorrectionId) > -1;
	}

	useInExportCorrection(brightnessCorrectionId: string) {
		if (!this.hasBrightnessCorrection(brightnessCorrectionId)) {
			return false;
		} else {
			let brightness = this.getBrightnessCorrection(brightnessCorrectionId);
			switch (brightness.state.tag) {
				case 'gamma':
					const exp =
						this.getBrightnessCorrection<CollectionModelGammaBrightnessCorrection>(brightnessCorrectionId).exponent;
					return exp !== 1;
				default:
					return false;
			}
		}
	}

	getBrightnessCorrection<T extends CollectionModelBrightnessCorrection = CollectionModelBrightnessCorrection>(
		brightnessCorrectionId: string
	): T {
		let brightnessCorrection = this.brightnessCorrections[
			brightnessCorrectionId
		] as CollectionModelBrightnessCorrection;
		if (brightnessCorrection == null) {
			let brightnessCorrectionState = this.collection.state.getFieldValue(
				CollectionModelBrightnessCorrectionState.brightnessCorrectionId(this.captureId, brightnessCorrectionId)
			) as ModelStateBrightnessCorrection; //FIXME
			if (brightnessCorrectionState == null) {
				//if we can't find a state with the current id, we recreate a new state with the same id, and query it again
				this.Mutator.create('gamma', brightnessCorrectionId);
				brightnessCorrectionState = this.collection.state.getFieldValue(
					CollectionModelBrightnessCorrectionState.brightnessCorrectionId(this.captureId, brightnessCorrectionId)
				) as ModelStateBrightnessCorrection;
			}

			switch (brightnessCorrectionState.tag) {
				case 'gamma':
					brightnessCorrection = new CollectionModelGammaBrightnessCorrection(
						this,
						brightnessCorrectionId,
						this.captureId
					);
					break;
				default:
					throw new Error(
						`Capture ${this.captureId}:Unknown brightness correction type: "${brightnessCorrectionState.tag}"`
					);
			}

			this.brightnessCorrections[brightnessCorrectionId] = brightnessCorrection;
		}

		return brightnessCorrection as T;
	}

	values(): CollectionModelBrightnessCorrection[] {
		const output: CollectionModelBrightnessCorrection[] = [];
		for (const brightnessCorrectionId of this.state) {
			const brightnessCorrection = this.getBrightnessCorrection(brightnessCorrectionId);
			output.push(brightnessCorrection);
		}
		return output;
	}

	useInExportCorrections(): boolean {
		let state = false;
		if (this.hasBrightnessCorrections()) {
			this.state.forEach((el) => {
				state = this.useInExportCorrection(el);
			});
		}
		return state;
	}

	hasBrightnessCorrections(): boolean {
		return this.state.length > 0;
	}

	get capture(): CaptureFileJSONObject {
		return this.collection.state.getCapture(this.captureId);
	}

	private get state(): ModelFieldStateBrightnessCorrection {
		return (
			(this.collection.state.getFieldValue(
				CollectionModelBrightnessCorrectionState.captureBrightnessCorrectionId(this.captureId)
			) as ModelFieldStateBrightnessCorrection) || []
		);
	}
}
